import { useState } from "react";
import { SiteHeader } from "@/components/marketing/SiteHeader";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { DepartureBoard } from "@/components/home/DepartureBoard";
import { HomeHero } from "@/components/home/HomeHero";
import { TrustLogosBar } from "@/components/home/TrustLogosBar";
import { AudienceToggle } from "@/components/home/AudienceToggle";
import {
  AudienceValueSection,
  type Audience,
} from "@/components/home/AudienceValueSection";
import { FeatureBentoSection } from "@/components/home/FeatureBentoSection";
import { HowItWorksSection } from "@/components/home/HowItWorksSection";
import { AboutSection } from "@/components/home/AboutSection";
import { PricingTeaserSection } from "@/components/home/PricingTeaserSection";
import { FaqSection } from "@/components/home/FaqSection";
import { FinalCtaSection } from "@/components/home/FinalCtaSection";
import { useHashScroll } from "@/hooks/useHashScroll";

/**
 * Section ids the homepage knows how to scroll to. The hash-scroll effect only
 * acts on this allowlist, so a hand-typed/escaped hash can never reach an
 * arbitrary selector. `contact` lives on the footer.
 */
const HOMEPAGE_ANCHORS = [
  "features",
  "how-it-works",
  "about",
  "pricing",
  "contact",
] as const;

/**
 * Public landing page — a dual-audience, AI-native B2B SaaS funnel that serves
 * both transportation companies and independent drivers.
 *
 * A thin composition shell. The shared, auth-aware {@link SiteHeader} and rich
 * {@link SiteFooter} bracket a single funnel-ordered `<main>` built from
 * self-contained section components.
 *
 * A single page-level `audience` state (default `companies`) is the site's one
 * audience control: a prominent {@link AudienceToggle} at the start of the
 * value area switches the merged value section, the pricing ladder, AND the FAQ
 * together, so the whole value area speaks to one reader at a time instead of
 * stacking two parallel pitches. The hero stays audience-neutral (both CTAs).
 *
 * Header/footer "Features / About / Contact" links are in-page anchors;
 * {@link useHashScroll} scrolls to the matching section on cold deep-link and on
 * in-page click. Anchored wrappers carry `scroll-mt-16` so the sticky header
 * never clips the scroll target.
 *
 * Stays public for everyone (signed-in or out) — no auth redirect.
 * @returns The home page component.
 */
export function HomePage() {
  const [audience, setAudience] = useState<Audience>("companies");
  useHashScroll(HOMEPAGE_ANCHORS);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />

      <main className="flex-1">
        <HomeHero visual={<DepartureBoard />} />

        <TrustLogosBar />

        {/* Audience-driven region. The page-level audience toggle is the single
            control for who the value / pricing / FAQ content addresses (never in
            the hero). It pins just below the sticky header as a slim bar so it
            follows the visitor through this whole region, then releases after the
            FAQ — `position: sticky` is contained by this wrapping <div>.

            `data-audience` scopes the accent palette: a CSS rule in globals.css
            remaps the cobalt `--color-primary-*` vars to the amber `live` family
            under `[data-audience="drivers"]`, so every primary-* accent below the
            hero recolors with the toggle (no per-component class churn).
            `audience` is a typed enum from local state, never user input. */}
        <div data-audience={audience}>
          <section aria-labelledby="audience-heading" className="bg-background">
            <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 px-4 pt-16 sm:px-6 lg:items-center lg:px-8 lg:pt-20 lg:text-center">
              <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
                Who are you?
              </span>
              <h2
                id="audience-heading"
                className="max-w-2xl text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl"
              >
                One platform, tuned to how you operate
              </h2>
              <p className="max-w-xl text-sm text-neutral-600 sm:text-base">
                Pick your lane — the value, pricing, and answers below follow
                your choice.
              </p>
            </div>
          </section>

          {/* Sticky toggle bar — pins under the SiteHeader (h-14) and stays
              reachable while scrolling. z-20 sits below the header's z-30. */}
          <div className="sticky top-14 z-20 border-y border-neutral-200 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75">
            <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-3 sm:px-6 lg:px-8">
              <AudienceToggle
                value={audience}
                onChange={setAudience}
                aria-labelledby="audience-heading"
              />
            </div>
          </div>

          <AudienceValueSection audience={audience} />

          {/* In-page anchors. scroll-mt clears BOTH the header and the pinned
              toggle bar so deep-linked sections aren't hidden behind them. */}
          <section id="features" className="scroll-mt-32">
            <FeatureBentoSection />
          </section>

          {/* In-page anchor targeted by the hero's "See how it works" jump. */}
          <section id="how-it-works" className="scroll-mt-32">
            <HowItWorksSection />
          </section>

          {/* In-page anchor: concise About band (replaces the old /about page). */}
          <section id="about" className="scroll-mt-32">
            <AboutSection />
          </section>

          {/* In-page anchor: pricing, driven by the page-level audience. */}
          <section id="pricing" className="scroll-mt-32">
            <PricingTeaserSection audience={audience} />
          </section>

          {/* FAQ, driven by the page-level audience. */}
          <FaqSection audience={audience} />
        </div>

        <FinalCtaSection />
      </main>

      {/* SiteFooter carries id="contact" — the "Contact" nav target. */}
      <SiteFooter />
    </div>
  );
}
